import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useDocumentData } from "react-firebase-hooks/firestore";
import { useParams } from "react-router-dom";
import { doc, setDoc, deleteDoc } from "firebase/firestore";
import { firestore } from "hooks/useFirebase";
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import { useSessionInspectorToggle } from "hooks/useSessionInspectorToggle";
import { logSession } from "utils/sentryLogger";
import { constants } from "utils";

export const getRef = (id) => {
  return id ? doc(firestore, `sessions/${id}`) : null;
};

export const useFirebaseRef = (sid) => {
  const { sessionId } = useParams();
  const id = sessionId || sid;
  return !id ? null : getRef(id);
};

export const useSession = (sid) => {
  const [sessionId, setSessionId] = useState(sid);
  const [groupCode, setGroupCode] = useState();
  const [userRole, setUserRole] = useState();
  const [authMethod, setAuthMethod] = useState();
  const [isStart, setIsStart] = useState();
  const [isStartObserving, setIsStartObserving] = useState();
  const [isCelebrating, setIsCelebrating] = useState();
  const [currentTransitionState, setCurrentTransitionState] = useState(constants.SOLO_TIME);
  const [currentEventState, setCurrentEventState] = useState(null);
  const [isShowTransitionView, setIsShowTransitionView] = useState(true);
  const { authUserData } = useAuthUserData();
  const ref = useFirebaseRef(sessionId);
  const [session, loading, error] = useDocumentData(ref, { idField: "id" });
  const { isShowSessionInspector, toggleSessionInspector } = useSessionInspectorToggle();
  const [isLowBandwidth, setIsLowBandwidth] = useState();
  const [localSessionActivityType, setLocalSessionActivityType] = useState(null);
  const [waitingRoomInit, setWaitingRoomInit] = useState(null);
  const localActivityIdRef = useRef(null);
  const activityIdHistoryRef = useRef(new Set());

  useEffect(() => {
    localActivityIdRef.current = null;
    activityIdHistoryRef.current = new Set();
  }, [sessionId]);

  const userIsSessionAdmin = session?.creatorId === authUserData?.id;
  const url = "/";
  const activityId = session?.activityId || constants.ACTIVITY_ID_WAITING_ROOM;
  const sessionActivityType = session?.sessionActivityType || localSessionActivityType;

  // Entry stamp for the "OKO is preparing" stuck detectors. It lives here, above
  // the Activity tree, so a connection blip (Join screen swapped in, WaitingRoom
  // remounted) cannot restart the clock; only a genuine screen or session change
  // does. Reset during render, not in an effect: effects run children-first, so
  // an effect here would wipe the stamp WaitingRoom's mount effect just wrote.
  const waitingRoomEnteredAtRef = useRef(null);
  const screenKeyRef = useRef();
  const screenKey = `${sessionId}:${activityId}`;
  if (screenKeyRef.current !== screenKey) {
    screenKeyRef.current = screenKey;
    waitingRoomEnteredAtRef.current = null;
  }

  const update = (data) => {
    if (data.activityId) {
      // Guard: if multiple-choice was already set, skip any waiting-room write
      if (
        data.activityId === constants.ACTIVITY_ID_WAITING_ROOM &&
        activityIdHistoryRef.current.has(constants.ACTIVITY_ID_MULTIPLE_CHOICE)
      ) {
        return Promise.resolve();
      }

      activityIdHistoryRef.current.add(data.activityId);
      localActivityIdRef.current = data.activityId;
    }
    if (ref) {
      return setDoc(ref, data, { merge: true });
    }
    return Promise.reject(new Error("No session reference available"));
  };
  
  const remove = () => {
    if (ref) {
      return deleteDoc(ref);
    }
    return Promise.reject(new Error("No session reference available"));
  };
  
  // Log when Firestore fallback is used for sessionActivityType
  useEffect(() => {
    if (!session?.sessionActivityType && localSessionActivityType) {
      logSession("Using local fallback for sessionActivityType", {
        sessionId,
        localSessionActivityType,
      });
    }
  }, [session?.sessionActivityType, localSessionActivityType, sessionId]);
  
  return {
    isStart,
    setIsStart,
    isStartObserving,
    setIsStartObserving,
    isCelebrating,
    setIsCelebrating,
    setSessionId,
    setGroupCode,
    groupCode,
    userRole,
    setUserRole,
    authMethod,
    setAuthMethod,
    session,
    activityId,
    sessionActivityType,
    setLocalSessionActivityType,
    ref,
    userIsSessionAdmin,
    code: session?.code,
    url,
    update,
    loading,
    error,
    remove,
    isShowSessionInspector,
    toggleSessionInspector,
    isLowBandwidth,
    setIsLowBandwidth,
    currentTransitionState,
    setCurrentTransitionState,
    currentEventState,
    setCurrentEventState,
    isShowTransitionView,
    setIsShowTransitionView,
    localActivityIdRef,
    activityIdHistoryRef,
    waitingRoomInit,
    setWaitingRoomInit,
    waitingRoomEnteredAtRef,
  };
};

const Context = createContext();

export const SessionProvider = ({ children }) => {
  const value = useSession();
  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useSessionContext = () => {
  return useContext(Context);
};
